// How the Visitors tab words its numbers. Pure, so each rule is testable without rendering. (A visit is shown as the Sessions tab's own row, which
// formats its own times and duration, so this file only words what the visitor rows themselves say.)

// A visitor has no name (they are anonymous), so a short tag tells two otherwise-identical visitors apart: "Visitor #A3F9".
export const shortVisitorId = (key: string | null | undefined): string => {
  const tail = String(key ?? "").replace(/[^A-Za-z0-9]/g, "").slice(-4).toUpperCase();

  return tail ? `#${tail}` : "";
};

// "just now", "5 min ago", "3 h ago", "2 d ago", then the date. For the row's "last seen".
export const formatLastSeen = (iso: string | null | undefined, now: Date = new Date(), locale?: string): string => {
  if (!iso) {
    return "";
  }
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) {
    return "";
  }
  const minutes = Math.floor((now.getTime() - then) / 60000);
  if (minutes < 1) {
    return "just now";
  }
  if (minutes < 60) {
    return `${minutes} min ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} h ago`;
  }
  const days = Math.floor(hours / 24);
  if (days < 7) {
    return `${days} d ago`;
  }

  return new Intl.DateTimeFormat(locale, { month: "short", day: "numeric" }).format(new Date(then));
};

// "Germany · Chrome · Mac", skipping whatever is unknown.
export const visitorDescription = (v: { country_name?: string | null; browser?: string | null; os?: string | null }): string =>
  [v.country_name, v.browser, v.os].filter((part) => typeof part === "string" && part && part.toUpperCase() !== "UNKNOWN").join(" · ");

// A visitor's visits span days, and a Sessions row only shows a TIME: the Sessions tab gets its date from the blue date bars it groups by. The Visitors tab does the
// same (Chris, 2026-10-12: "C2 - indented I like"): a visitor's visits are grouped under one date bar per day, in the Sessions tab's own bar format
// ("October 11, 2026", the year always shown), so a row carries only its times. Inline dates were measured first: with a year they wrap to two lines for most visits.
//
// Days are the VIEWER'S local calendar days, like playback's and the Sessions tab's.
const pad2 = (n: number) => String(n).padStart(2, "0");

export const localDayKey = (iso: string | null | undefined): string | null => {
  if (!iso) {
    return null;
  }
  const d = new Date(iso);

  return Number.isNaN(d.getTime()) ? null : `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

// "2026-10-11" -> "October 11, 2026": exactly what the Sessions tab's date bar shows for the same day.
export const dayBarLabel = (dayKey: string | null): string => {
  const m = typeof dayKey === "string" ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(dayKey) : null;
  if (!m) {
    return "";
  }

  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};

export interface DayGroup<T> {
  key: string; // the local day, or "unknown" when a visit has no usable start time
  label: string; // the bar's text; empty for "unknown", which gets no bar
  items: Array<{ item: T; index: number }>; // index = the item's position in the list that was grouped (the caller numbers visits by it)
}

// Group a NEWEST-FIRST list into consecutive runs of the same day, keeping each item's original index.
export const groupByDay = <T extends { createdAt?: string }>(list: T[]): Array<DayGroup<T>> => {
  const groups: Array<DayGroup<T>> = [];
  list.forEach((item, index) => {
    const key = localDayKey(item.createdAt) ?? "unknown";
    const last = groups[groups.length - 1];
    if (last && last.key === key) {
      last.items.push({ item, index });
    } else {
      groups.push({ key, label: dayBarLabel(key), items: [{ item, index }] }); // "unknown" is no day, so its label is empty and it gets no bar
    }
  });

  return groups;
};

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

// What a row shows in its two time fields UNDER a day bar: the start is just its time (the bar says the day), and the end is just its time too, unless the visit
// crossed midnight, in which case the end carries its own date so it can never read as an earlier time on the same day. Its year is shown only if it differs from the
// start's (the bar already says the start's), which keeps the row to one line. Built on the times the shared formatter already produced (formatSessionForRow), so a
// time is formatted exactly as in the Sessions tab. `locale` is injectable so the tests do not depend on the machine.
export const rowTimesInDay = (
  session: { createdAt?: string; session_end_time?: string; visit_time?: string; exit_time?: string },
  locale?: string
): { visit_time: string | undefined; exit_time: string | undefined } => {
  const started = session.createdAt ? new Date(session.createdAt) : null;
  const ended = session.session_end_time ? new Date(session.session_end_time) : null;
  const validStart = started && !Number.isNaN(started.getTime()) ? started : null;
  const validEnd = ended && !Number.isNaN(ended.getTime()) ? ended : null;
  if (!(validStart && validEnd && session.exit_time) || sameDay(validStart, validEnd)) {
    return { visit_time: session.visit_time, exit_time: session.exit_time };
  }
  const endDate = new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    ...(validStart.getFullYear() === validEnd.getFullYear() ? {} : { year: "numeric" }),
  }).format(validEnd);

  return { visit_time: session.visit_time, exit_time: `${endDate}, ${session.exit_time}` };
};

// "WATCH THIS VISIT" (the Visitors tab): which playback day a visit belongs to and where in it to start. Playback days are the viewer's LOCAL calendar
// days, so the date is the local date of the visit's start, and the offset is milliseconds since that local midnight (the scrub bar's unit). It starts a few
// seconds BEFORE the visit began, so pressing play shows the visitor arriving instead of already being there.
//
// `dayStartMs` is playbackService's own localDayStartMs, passed in so there is ONE definition of "local midnight" (that file's comment says every caller must
// match it exactly); the tests pass an equivalent.
export const WATCH_LEAD_IN_MS = 10_000;

export const watchTarget = (
  startedAtIso: string | null | undefined,
  dayStartMs: (date: string) => number,
  leadInMs: number = WATCH_LEAD_IN_MS
): { date: string; realOffsetMs: number } | null => {
  if (!startedAtIso) {
    return null;
  }
  const started = new Date(startedAtIso);
  if (Number.isNaN(started.getTime())) {
    return null;
  }
  const date = [started.getFullYear(), String(started.getMonth() + 1).padStart(2, "0"), String(started.getDate()).padStart(2, "0")].join("-");

  return { date, realOffsetMs: Math.max(0, started.getTime() - dayStartMs(date) - leadInMs) };
};

// Playback may reach a visit unless the plan limits how far back it goes (Base Jump's one day) and the visit is older than that. Paid tiers have no
// playback floor beyond their retention, which the visits list is already clamped to.
export const isWatchable = (
  startedAtIso: string | null | undefined,
  playbackAllowed: boolean,
  earliestPlaybackDate: Date | null
): boolean => {
  if (!playbackAllowed || !startedAtIso) {
    return false;
  }
  const started = new Date(startedAtIso);
  if (Number.isNaN(started.getTime())) {
    return false;
  }

  return !earliestPlaybackDate || started.getTime() >= earliestPlaybackDate.getTime();
};

// Text that goes into a tooltip is injected as HTML (react-tooltip's data-tooltip-html), and place names come from the database, so every value is escaped.
export const escapeHtml = (value: unknown): string =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

// The master visitor record's flag tooltip: the same lines as a Sessions row's flag (country, then region and city only when they resolved, never "UNKNOWN"),
// for the visitor's LATEST visit, plus the other countries it has been seen in. A flag alone says only where the latest visit was.
// "DE" -> "Germany" in English (the stored names are English), or "" when the code is not a real ISO country code. Never invents a name.
export const countryNameFromCode = (code?: string | null): string => {
  if (typeof code !== "string" || !/^[A-Za-z]{2}$/.test(code)) {
    return "";
  }
  try {
    const name = new Intl.DisplayNames(["en"], { type: "region" }).of(code.toUpperCase());

    // ICU answers a code it does not know with the literal "Unknown Region" (ZZ, XX, ...), which is not a name.
    return name && name.toUpperCase() !== code.toUpperCase() && !/^unknown region$/i.test(name) ? name : "";
  } catch (e) {
    return "";
  }
};

export const visitorGeoLines = (
  visitor: { country_name?: string | null; country_code?: string | null; region_name?: string | null; city?: string | null; otherCountries?: string[] | null; visitor_category?: string | null },
  label: { country: string; region: string; city: string; alsoSeenIn: string; unknown: string; unknownBot?: string },
  isResolved: (value?: string | null) => boolean
): string[] => {
  const known = (value?: string | null) => typeof value === "string" && value.length > 0 && value.toUpperCase() !== "UNKNOWN";
  // 🔴 ALWAYS Country, Region and City, in that order, with "Unknown" for whichever the lookup could not resolve (Chris, 2026-10-13: "Even if it wasn't available, shouldn't it say
  // 'Region: Unknown. City: Unknown'?"). They used to be OMITTED when unresolved, which read as a tooltip that was missing its lines rather than a lookup with no answer. The geo dataset
  // is country-only for many IP ranges (measured: a Singapore visit on production is stored as region "UNKNOWN", city "UNKNOWN"), so this is common, and "Unknown" is the truth.
  // What a missing value reads as. Not the word "Unknown", which says Skydive CANNOT know and reads as inaccurate (Chris, 2026-10-15): the lookup simply has no record for this address, and
  // for a visitor we have CLASSIFIED as a bot (AI crawler, search-engine bot, other bot) we say so, because that is a fact we hold and bot traffic rarely has a city to find. "Datacenter" is
  // deliberately NOT claimed: nothing here knows who owns the network.
  const isBot = ["ai_crawler", "search_engine_bot", "other_bot"].includes(String(visitor.visitor_category ?? ""));
  const missing = escapeHtml(isBot && label.unknownBot ? label.unknownBot : label.unknown);
  const part = (value?: string | null) => (isResolved(value) ? escapeHtml(value) : missing);
  // The flag is drawn from the country CODE, so a row can show a German flag while its stored NAME is missing - and "Country: Unknown" beside a flag contradicts itself (measured: ~5,200
  // staging rows have a code and no name). When the name is missing the code is spelled out; only when both are missing is it Unknown.
  const countryText = known(visitor.country_name) ? visitor.country_name : countryNameFromCode(visitor.country_code);
  const lines = [
    `${escapeHtml(label.country)}: ${countryText ? escapeHtml(countryText) : missing}`,
    `${escapeHtml(label.region)}: ${part(visitor.region_name)}`,
    `${escapeHtml(label.city)}: ${part(visitor.city)}`,
  ];
  const others = (visitor.otherCountries ?? []).filter(known);
  if (others.length) {
    lines.push(`${escapeHtml(label.alsoSeenIn)}: ${others.map(escapeHtml).join(", ")}`);
  }

  return lines;
};

// When a peer was active, in the app's date style with the year always shown: "Aug 29, 2026", "Aug 16 - Aug 29, 2026", "Dec 30, 2025 - Jan 2, 2026" (local days).
export const activeRange = (firstIso: string | null | undefined, lastIso: string | null | undefined, locale?: string): string => {
  const a = firstIso ? new Date(firstIso) : null;
  const b = lastIso ? new Date(lastIso) : null;
  if (!a || !b || Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) {
    return "";
  }
  const withYear = (d: Date) => new Intl.DateTimeFormat(locale, { month: "short", day: "numeric", year: "numeric" }).format(d);
  const noYear = (d: Date) => new Intl.DateTimeFormat(locale, { month: "short", day: "numeric" }).format(d);
  if (sameDay(a, b)) {
    return withYear(a);
  }

  return a.getFullYear() === b.getFullYear() ? `${noYear(a)} \u2013 ${withYear(b)}` : `${withYear(a)} \u2013 ${withYear(b)}`;
};

// One line about another visitor on the same IP, from what was MEASURED: its browser (against this visitor's, when both are readable), how many visits, when it was
// active, and how that sits against this visitor's activity. Parts that are unknown are left out, never guessed. `t` looks up the copy.
export const describePeer = (
  peer: { visits: number; firstSeen: string; lastSeen: string; browser: string | null; browserDiffers: boolean | null; relation: string },
  thisBrowser: string | null,
  t: (key: string) => string
): string => {
  let browser: string | null = null;
  if (peer.browser) {
    if (peer.browserDiffers === true && thisBrowser) {
      browser = `${peer.browser} (${t("peer_this_one")}: ${thisBrowser})`;
    } else if (peer.browserDiffers === false) {
      browser = `${peer.browser} (${t("peer_same_as_this")})`;
    } else {
      browser = peer.browser;
    }
  }
  const relation = ["earlier", "later", "alternates", "overlaps"].includes(peer.relation) ? t(`peer_${peer.relation}`) : null;
  const visits = `${peer.visits} ${peer.visits === 1 ? t("visit_singular") : t("visit_plural")}`;

  return [browser, visits, activeRange(peer.firstSeen, peer.lastSeen), relation].filter((part) => part).join(" \u00b7 ");
};
